import type { ReactNode } from 'react';

/**
 * Tertiary metadata text — small, gray, tabular numerals when
 * containing numbers. Used for things like "5 abilities · 12.4k
 * calls", post counters, sidebar stats.
 *
 * @example
 * <MetaText>{count} 个 ability · {formatCount(calls)} calls</MetaText>
 */
export function MetaText({ children }: { children: ReactNode }) {
  return (
    <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
      {children}
    </span>
  );
}

/**
 * A timestamp — same color as MetaText but always tabular and
 * truncates without a tooltip. Use for "5m ago", "Yesterday",
 * "Feb 15".
 *
 * @example
 * <TimeStamp>{formatRelative(post.createdAt)}</TimeStamp>
 */
export function TimeStamp({ children }: { children: ReactNode }) {
  return (
    <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums whitespace-nowrap">
      {children}
    </span>
  );
}

/**
 * Inline middle dot separator — `text-gray-300 dark:text-gray-600`,
 * non-selectable. Used between meta items: "Top · Claude · 1.2s".
 *
 * @example
 * <span>Top</span><Dot/><span>Claude</span><Dot/><span>1.2s</span>
 */
export function Dot() {
  return <span className="text-gray-300 dark:text-gray-600 select-none">·</span>;
}
